// =====================================================================
// Tutor payout ("earned in period") helpers — ADMIN-ONLY money math.
//
// Owed to a tutor = Σ over payable sessions of
//   effectiveRate(student override, tutor rate) × minutes / 60
//
// There is no payment tracking: "owed" always means "earned in the
// selected period". Rates live in the admin-only tutor_billing /
// student_billing tables (RLS-locked), so non-admins can never resolve
// an amount even if they reached this code.
//
// Pure functions only — no Supabase, so TutorDetail and Tutors share
// identical math and everything is unit-testable.
// =====================================================================

import { startOfMonth, addMonths, format } from 'date-fns'
import { getEffectiveRate } from './rates'

/** Minimal structural session shape — page row types satisfy it. */
export type PayableSessionLike = {
  student_id: string
  started_at: string
  minutes: number
  approval_status: 'pending' | 'approved' | 'rejected'
  lifecycle_status: 'completed' | 'cancelled'
  is_free: boolean
}

/**
 * Free (consulting-credit) sessions are free for the STUDENT, but the tutor
 * still worked the hours — so they count toward pay. Flip this single
 * constant to exclude them from owed amounts.
 */
export const PAY_FREE_SESSIONS = true

/** Delivered work: approved and not cancelled. Drives hours/charts. */
export function isDelivered(s: PayableSessionLike): boolean {
  return s.approval_status === 'approved' && s.lifecycle_status !== 'cancelled'
}

/** Counts toward money owed to the tutor. */
export function isPayable(s: PayableSessionLike): boolean {
  return isDelivered(s) && (PAY_FREE_SESSIONS || !s.is_free)
}

/** Defensive duration: corrupt rows with negative/NaN minutes count as 0. */
export function safeMinutes(minutes: number | null | undefined): number {
  const m = Number(minutes)
  if (!Number.isFinite(m) || m < 0) return 0
  return m
}

export type OwedResult = {
  /** USD earned in the period. */
  total: number
  /** Payable sessions included. */
  payableCount: number
  /** Clamped minutes included. */
  payableMinutes: number
  /** Payable sessions whose effective rate resolved to <= 0 (contribute $0). */
  missingRateCount: number
}

/**
 * Owed = Σ over payable sessions of effectiveRate × safeMinutes / 60.
 * `overrideByStudent` maps student_id → hourly_rate_override (null when the
 * student has no override). Sessions with no resolvable rate contribute $0
 * and are surfaced via `missingRateCount` so the UI can hint instead of
 * silently under-reporting. (An intentional $0 override lands there too —
 * a zero pay rate is treated as "not set".)
 */
export function computeOwed(
  sessions: readonly PayableSessionLike[],
  overrideByStudent: ReadonlyMap<string, number | null>,
  tutorRate: number | null | undefined
): OwedResult {
  let total = 0
  let payableCount = 0
  let payableMinutes = 0
  let missingRateCount = 0
  for (const s of sessions) {
    if (!isPayable(s)) continue
    payableCount++
    const minutes = safeMinutes(s.minutes)
    payableMinutes += minutes
    const rate = getEffectiveRate(overrideByStudent.get(s.student_id) ?? null, tutorRate ?? null)
    if (rate <= 0) { missingRateCount++; continue }
    total += rate * (minutes / 60)
  }
  return { total, payableCount, payableMinutes, missingRateCount }
}

export type TutorSessionLike = PayableSessionLike & { tutor_id: string }

/** Per-tutor owed amounts over a set of (already period-filtered) sessions. */
export function computeOwedByTutor(
  sessions: readonly TutorSessionLike[],
  overrideByStudent: ReadonlyMap<string, number | null>,
  ratesByTutor: ReadonlyMap<string, number | null>
): Map<string, OwedResult> {
  const byTutor = new Map<string, TutorSessionLike[]>()
  for (const s of sessions) {
    const list = byTutor.get(s.tutor_id)
    if (list) list.push(s)
    else byTutor.set(s.tutor_id, [s])
  }
  const result = new Map<string, OwedResult>()
  byTutor.forEach((rows, tutorId) => {
    result.set(tutorId, computeOwed(rows, overrideByStudent, ratesByTutor.get(tutorId) ?? null))
  })
  return result
}

export type MonthBucket = {
  /** 'yyyy-MM' in local time, consistent with the app's date-fns usage. */
  key: string
  /** e.g. 'Sep 25' */
  label: string
  sessions: number
  minutes: number
  hours: number
}

const MONTH_KEY = 'yyyy-MM'

/**
 * Buckets sessions by calendar month of started_at, zero-filling every month
 * between window.from and window.to (each truncated to its month). Null/absent
 * bounds are resolved from the data (earliest/latest session). Returns [] when
 * there are no sessions and no complete window. Callers pre-filter to
 * delivered sessions.
 */
export function groupByMonth(
  sessions: readonly PayableSessionLike[],
  window?: { from?: Date | null; to?: Date | null }
): MonthBucket[] {
  let from = window?.from ? startOfMonth(window.from) : null
  let to = window?.to ? startOfMonth(window.to) : null
  if (!from || !to) {
    let earliest: Date | null = null
    let latest: Date | null = null
    for (const s of sessions) {
      const d = new Date(s.started_at)
      if (!earliest || d < earliest) earliest = d
      if (!latest || d > latest) latest = d
    }
    from = from ?? (earliest ? startOfMonth(earliest) : null)
    to = to ?? (latest ? startOfMonth(latest) : null)
  }
  if (!from || !to || from > to) return []
  // Safety cap: a corrupt ancient started_at must not zero-fill thousands of
  // buckets and freeze the chart. Keep the most recent 20 years.
  const MAX_MONTHS = 240
  const cap = addMonths(to, -(MAX_MONTHS - 1))
  if (from < cap) from = cap

  const buckets = new Map<string, MonthBucket>()
  for (let m = from; m <= to; m = addMonths(m, 1)) {
    buckets.set(format(m, MONTH_KEY), {
      key: format(m, MONTH_KEY),
      label: format(m, 'MMM yy'),
      sessions: 0,
      minutes: 0,
      hours: 0,
    })
  }
  for (const s of sessions) {
    const bucket = buckets.get(format(new Date(s.started_at), MONTH_KEY))
    if (!bucket) continue // outside the window
    bucket.sessions++
    bucket.minutes += safeMinutes(s.minutes)
    bucket.hours = bucket.minutes / 60
  }
  return Array.from(buckets.values())
}

export type StudentBucket = {
  studentId: string
  sessions: number
  minutes: number
  hours: number
  /** 0..1 fraction of the total minutes (0 when total is 0 — never NaN). */
  share: number
}

/** Groups sessions per student, sorted by minutes desc. Callers pre-filter to delivered. */
export function groupByStudent(sessions: readonly PayableSessionLike[]): StudentBucket[] {
  const buckets = new Map<string, StudentBucket>()
  let totalMinutes = 0
  for (const s of sessions) {
    let bucket = buckets.get(s.student_id)
    if (!bucket) {
      bucket = { studentId: s.student_id, sessions: 0, minutes: 0, hours: 0, share: 0 }
      buckets.set(s.student_id, bucket)
    }
    const minutes = safeMinutes(s.minutes)
    bucket.sessions++
    bucket.minutes += minutes
    bucket.hours = bucket.minutes / 60
    totalMinutes += minutes
  }
  const result = Array.from(buckets.values()).sort((a, b) => b.minutes - a.minutes)
  if (totalMinutes > 0) result.forEach((b) => { b.share = b.minutes / totalMinutes })
  return result
}
